import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataGrid, { Column, Toolbar, Item } from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import styled from "styled-components";
import {
  fetchInvitations,
  addInvitation,
  updateInvitation,
  extendInvExpiry,
  revokeInvitation,
  sendInvitation,
  sendReminder,
} from "../Redux/feasibility";
import AddSiteModal from "./AddSiteModal";
import EmailLogModal from "./EmailLogModal";
import EmailTemplateModal from "./EmailTemplateModal";
import ConsentTemplateModal from "./ConsentTemplateModal";
import ReminderTemplateModal from "./ReminderTemplateModal";
import SendInvitationModal from "./SendInvitationModal";
import SendReminderModal from "./SendReminderModal";
import RevokeLinkModal from "./RevokeLinkModal";
import { effectiveExpiry, expiryWillRenew } from "./emailTemplates";
import { REMINDER_ELIGIBLE_STATUS, reminderState } from "./reminderTemplates";
import { sfqApi } from "../../../api/feasibility";
import { isStudyFrozen } from "../FeasibilityEditor/data";

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #212529;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => STATUS_STYLE[p.$s]?.bg || "#e9ecef"};
  color: ${(p) => STATUS_STYLE[p.$s]?.color || "#495057"};
`;

const VerBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #e7f1ff;
  color: #0d6efd;
  font-weight: 600;
  font-size: 11px;
`;

const RiskBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => ({ High: "#dc3545", Medium: "#fd7e14", Low: "#198754" })[p.$r] || "#6c757d"};
`;

const Muted = styled.span`
  color: #adb5bd;
`;

// A date that has passed is not just a date: the link that site holds answers "expired"
// until somebody moves it or sends again. Worth saying on the row rather than leaving the
// CRA to compare the column against today's date.
//
// $revoked: the same closed link, but closed on purpose. The date alone cannot tell the
// two apart — a revoke sets the expiry to the moment it happened — and the difference is
// exactly what the CRA who just clicked 회수 is looking for.
const ExpiredDate = styled.span`
  font-size: 12px;
  color: #b02a37;
  font-weight: 600;
  &::after {
    content: ${(p) => (p.$revoked ? '"회수됨"' : '"만료"')};
    margin-left: 5px;
    padding: 1px 5px;
    border-radius: 8px;
    background: #f8d7da;
    font-size: 10px;
    font-weight: 700;
  }
`;

// $expired: 재발송이 곧 "닫힌 링크를 다시 여는" 동작이 되는 행. Expires 칼럼의 만료
// 배지와 같은 색을 써서 두 칼럼이 같은 사실을 말하고 있음을 드러낸다.
const SendBtn = styled.button`
  padding: 3px 10px;
  border: 1px solid ${(p) => (p.$expired ? "#b02a37" : "#0d6efd")};
  border-radius: 4px;
  background: #fff;
  color: ${(p) => (p.$expired ? "#b02a37" : "#0d6efd")};
  font-size: 12px;
  font-weight: ${(p) => (p.$expired ? 600 : 400)};
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: ${(p) => (p.$expired ? "#f8d7da" : "#e7f1ff")}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const EditBtn = styled.button`
  padding: 3px 9px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  margin-right: 6px;
  &:hover { background: #f1f3f5; }
`;

// 재촉은 발송이지만 초대 발송은 아니다. Send의 파란색을 쓰지 않는 이유가 그것이다 —
// 나란히 놓인 두 버튼이 같은 색이면 "Resend를 눌러도 비슷한 일이 벌어지겠거니" 하고
// 누르게 되고, Resend는 그 사이트의 비밀번호를 갈아 치운다. 회색 외곽선의 Edit 쪽에
// 붙여 두어 "가벼운 동작"으로 읽히게 한다.
const RemindBtn = styled.button`
  padding: 3px 9px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  /* Send 가 이 줄의 기준점이라 자기 여백을 갖지 않는다. 그 앞의 Edit 만 margin-right 로
     간격을 만들고, 뒤에 오는 것들(알림·회수·✉)은 전부 margin-left 로 붙는다.
     여기서 margin-right 를 쓰면 왼쪽은 0, 오른쪽은 회수의 margin-left 와 겹쳐 12px 이 된다. */
  margin-left: 6px;
  &:hover { background: #f1f3f5; }
`;

// 알림 칼럼. 켜져 있으면 주기와 진행("7일마다 · 2/3"), 꺼져 있으면 왜 꺼졌는지.
const ReminderCell = styled.span`
  font-size: 11px;
  color: ${(p) => (p.$off ? "#adb5bd" : p.$done ? "#6c757d" : "#495057")};
  ${(p) => (p.$custom ? "font-weight:600;" : "")}
`;

// Same red as the 만료/회수됨 badge and the expired Send button: all three are about a
// link being shut. Outlined rather than filled — this sits in a row of small actions, and
// the weight belongs on the confirm button inside the modal, not on the way in.
const RevokeBtn = styled.button`
  padding: 3px 9px;
  border: 1px solid #b02a37;
  border-radius: 4px;
  background: #fff;
  color: #b02a37;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 6px;
  &:hover { background: #f8d7da; }
`;

const MailBtn = styled.button`
  padding: 3px 8px;
  border: 1px solid ${(p) => p.$sent ? "#198754" : "#dee2e6"};
  border-radius: 4px;
  background: ${(p) => p.$sent ? "#d1e7dd" : "#f8f9fa"};
  color: ${(p) => p.$sent ? "#0a3622" : "#adb5bd"};
  font-size: 12px;
  cursor: ${(p) => p.$sent ? "pointer" : "default"};
  white-space: nowrap;
  margin-left: ${(p) => p.$solo ? "0" : "6px"};
  &:hover { filter: ${(p) => p.$sent ? "brightness(0.95)" : "none"}; }
`;

const STATUS_STYLE = {
  Pending:   { bg: "#e9ecef", color: "#495057" },
  Sent:      { bg: "#cfe2ff", color: "#084298" },
  InProgress:{ bg: "#fff3cd", color: "#664d03" },
  Submitted: { bg: "#d1e7dd", color: "#0a3622" },
  Reviewed:  { bg: "#d0d6f9", color: "#1d3367" },
  Finalized: { bg: "#d1ecf1", color: "#0c5460" },
};

const STATUS_LABELS = {
  Pending:   "Pending",
  Sent:      "Sent",
  InProgress:"In Progress",
  Submitted: "Submitted",
  Reviewed:  "Reviewed",
  Finalized: "Finalized",
};

// Status/Risk 정렬은 알파벳순이 아니라 진행 단계 순서를 따른다.
const STATUS_ORDER = { Pending: 0, Sent: 1, InProgress: 2, Submitted: 3, Reviewed: 4, Finalized: 5 };
const RISK_ORDER   = { Low: 0, Medium: 1, High: 2 };

// 사이트가 아직 설문을 작성해야 하는 단계에서만 발송/재발송이 성립한다.
const SENDABLE_STATUS = ["Pending", "Sent", "InProgress"];

// 회수는 "이미 나간 링크를 닫는" 일이다. Pending 행은 아직 아무것도 보내지 않았으므로 닫을
// 링크가 없고, 제출 이후의 행은 이미 받아둔 답변의 기록이다. API의 REVOCABLE_STATUS와 같다.
const REVOCABLE_STATUS = ["Sent", "InProgress"];

// 제출 전까지만 등록 정보를 고칠 수 있다. 제출 이후의 행은 "누가 이 답변을 냈는가"에
// 대한 기록이므로 손대지 않는다. 발송 이후에는 담당자 정보와 만료일만 열리며, 어떤
// 항목이 열리는지는 API(INV_EDITABLE_FIELDS)가 최종적으로 판단한다.
const EDITABLE_STATUS = ["Pending", "Sent", "InProgress"];

// Sites can be lined up while the study is still being written, but nothing may leave the
// building until the internal review has approved it (Active): before that there is no
// published version to pin, so a send would mail out an unapproved draft.
// The API's markInvitationSent rejects these statuses too. The other end of the study's
// life closes the same door — see mailBlocked below.
const PRE_APPROVAL_STATUS = ["Draft", "InternalReview"];

const siteLabel = (row) => row.siteName || row.siteCode || null;

const fmtDate = (d) =>
  d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });

// 이미 발송된 사이트가 들고 있는 링크가 지금은 열리지 않는 상태. Pending 행은 아직 아무
// 것도 받지 않았으므로 만료를 따지지 않는다 — 그 행의 발송은 첫 링크를 주는 일이고,
// 지나간 만료일은 발송이 알아서 갱신한다.
const isLinkExpired = (inv) => inv.status !== "Pending" && expiryWillRenew(inv);

// `canWork` is being the study's owner (createdBy) or one of its assigned CRAs
// (SFQ_CRA rows) — StudyView derives it, since it is the side that holds the study row.
// The assignment itself is what counts, not a permission: only SFQ_CRA_EDIT holders can
// be assigned in the first place. The API's craWorkGate enforces the same.
// craEnabled: 이 Study가 CRA Assessment를 쓰는지. 끄면 Risk/Score는 영영 비어 있으므로
// 두 칼럼을 아예 내린다 — 채워질 리 없는 "—"만 남기는 편이 더 헷갈린다.
export default function SiteInvitationList({ sfqId, onSelect, sfqStatus, canWork,
                                             craEnabled = true }) {
  const dispatch    = useDispatch();
  const invitations = useSelector((s) => s.feasibility.invitations);
  const loading     = useSelector((s) => s.feasibility.invitationsLoading);
  const contentDirty= useSelector((s) => s.feasibility.currentSFQ?.contentDirty);
  const myName      = useSelector((s) => s.userInfo.name);

  // Too early (no approved version to send) or too late (the study is closed out).
  // `canWork` already arrives false when frozen, but the composer is reachable from the
  // row itself, so the mail path checks the status on its own.
  const mailBlocked = PRE_APPROVAL_STATUS.includes(sfqStatus) || isStudyFrozen(sfqStatus);

  const [showAdd, setShowAdd]           = useState(false);
  const [editInv, setEditInv]           = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showConsent, setShowConsent]   = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [composeInv, setComposeInv]     = useState(null);
  const [remindInv, setRemindInv]       = useState(null);
  const [emailLogInv, setEmailLogInv]   = useState(null);
  const [revokeInv, setRevokeInv]       = useState(null);

  // 스터디의 자동 알림 주기. 행마다 "7일마다 · 2/3"을 계산하려면 사이트 쪽 예외만으로는
  // 모자라고 이 기본값이 있어야 한다. Redux에 두지 않는 이유는 이 목록 말고 읽는 곳이
  // 없고, 템플릿/동의문 모달도 같은 방식으로 sfqApi를 직접 부르기 때문이다.
  const [reminderSettings, setReminderSettings] = useState(null);

  useEffect(() => {
    dispatch(fetchInvitations(sfqId));
  }, [sfqId, dispatch]);

  // 실패해도 조용히 넘어간다: 알림 설정을 못 읽으면 알림 칼럼이 "—"로 남을 뿐, 목록 자체는
  // 초대 관리를 위해 열려 있어야 한다.
  useEffect(() => {
    if (!canWork) return undefined;
    let alive = true;
    sfqApi.getReminderSettings(sfqId)
      .then((res) => { if (alive) setReminderSettings(res.data.data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [sfqId, canWork]);

  // A rejected add leaves the modal open with what was typed still in it, so the CRA can
  // fix what the message names instead of retyping the row.
  async function handleAdd(data) {
    try {
      await dispatch(addInvitation({ sfqId, data })).unwrap();
      setShowAdd(false);
    } catch (err) {
      alert(err?.message || "사이트 추가에 실패했습니다.");
    }
  }

  // Two writes, because they are two different things: the site's details, and how long
  // the link it was given stays alive. The expiry goes through its own route so the site
  // keeps the password it already has — a resend would replace it. Only the parts that
  // actually moved are sent; the API is the one that decides which are still editable.
  async function handleEdit({ expiry, ...fields }) {
    const inv = editInv;
    try {
      const expiryMoved =
        !inv.siteTokenExpiry ||
        new Date(expiry).getTime() !== new Date(inv.siteTokenExpiry).getTime();

      await dispatch(updateInvitation({ sfqId, invId: inv.id, data: fields })).unwrap();
      if (expiryMoved) {
        await dispatch(extendInvExpiry({ sfqId, invId: inv.id, expiry })).unwrap();
      }
      setEditInv(null);
    } catch (err) {
      alert(err?.message || "사이트 정보 수정에 실패했습니다.");
    }
  }

  // Opening the composer is where the unpublished-changes warning belongs: it is the
  // last point before the CRA starts writing a mail against a version that isn't the
  // one they are looking at. An expired row gets its own warning here for the same
  // reason: the CRA is about to reopen a link the site has been failing to use, and the
  // send silently moves the expiry — better said before the mail is written than after.
  function openCompose(inv) {
    if (mailBlocked) return;
    if (isLinkExpired(inv)) {
      const expired = inv.siteTokenExpiry ? new Date(inv.siteTokenExpiry) : null;
      // A revoked row was closed by somebody on purpose, and the CRA reopening it should
      // be told that rather than being shown a date as if the link had merely lapsed —
      // the person who closed it may not be the person now sending.
      const opening = inv.revokedAt
        ? `이 사이트의 링크는 ${fmtDate(new Date(inv.revokedAt))}에 ` +
          `${inv.revokedByName ? `${inv.revokedByName}님이 ` : ""}회수하여 지금 열리지 않습니다.`
        : expired && !isNaN(expired.getTime())
        ? `이 사이트의 링크는 만료일(${fmtDate(expired)})이 지나 지금 열리지 않습니다.`
        : "이 사이트의 링크는 만료일이 없어 지금 열리지 않습니다.";
      if (!window.confirm(
        `${opening}\n` +
        `재발송하면 만료일이 ${fmtDate(effectiveExpiry(inv))}로 갱신되고, ` +
        "새 비밀번호가 발급됩니다. 이전 비밀번호는 더 이상 쓸 수 없습니다.\n\n" +
        "[확인] 메일 작성으로 이동   /   [취소] 중단\n" +
        "(발송 없이 기간만 늘리려면 Edit에서 만료일을 옮기세요.)"
      )) {
        return;
      }
    }
    if (inv.status === "Pending" && contentDirty && !window.confirm(
      "발행되지 않은 변경사항이 있습니다.\n" +
      "지금 발송하면 마지막으로 발행된 버전이 전송되며, 현재 편집 내용은 포함되지 않습니다.\n\n" +
      "[확인] 이대로 발송   /   [취소] 발송 중단 후 발행하기"
    )) {
      return;
    }
    setComposeInv(inv);
  }

  // The modal has already said what this does; a failure here is the API refusing (a row
  // that was submitted or revoked while the modal was open), so the message is the point.
  // The modal stays open on failure so its explanation is still on screen beside the error.
  async function handleRevoke(reason) {
    try {
      await dispatch(revokeInvitation({ sfqId, invId: revokeInv.id, reason })).unwrap();
      setRevokeInv(null);
    } catch (err) {
      alert(err?.message || "링크 회수에 실패했습니다.");
    }
  }

  // mail = { subject, bodyHtml, lang }, still holding {{password}}/{{publicUrl}}.
  async function handleSend(mail) {
    try {
      await dispatch(sendInvitation({ sfqId, invId: composeInv.id, ...mail })).unwrap();
      setComposeInv(null);
      alert("초대 메일이 발송되었습니다.");
    } catch (err) {
      // Email send failed → backend rolled back, invitation keeps its previous state.
      // Leave the composer open so the drafted message isn't lost on a retry.
      alert(err?.message || "초대 메일 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    }
  }

  // 알림은 상태를 바꾸지 않으므로 실패해도 되돌릴 것이 없다. 초대 발송과 달리 성공 후
  // 목록의 어느 칸도 달라지지 않는 것이 정상이라, alert이 유일한 피드백이다.
  async function handleSendReminder(mail) {
    try {
      await dispatch(sendReminder({ sfqId, invId: remindInv.id, ...mail })).unwrap();
      setRemindInv(null);
      alert("알림 메일이 발송되었습니다.");
    } catch (err) {
      alert(err?.message || "알림 메일 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    }
  }

  function buildLink(inv) {
    return `${window.location.origin}/sfq-site/${inv.siteToken || ""}`;
  }

  function handleRowClick(e) {
    // Actions 칼럼의 버튼 클릭이 행 선택으로 이어지지 않게 한다.
    if (e.rowType !== "data" || e.event?.target?.closest("button")) return;
    if (e.data.status !== "Pending" && onSelect) onSelect(e.data.id);
  }

  function renderDate(cell) {
    return cell.value ? <span style={{ fontSize: 12 }}>{cell.text}</span> : <Muted>—</Muted>;
  }

  // Expires: the same date, plus whether the site's link is actually open right now.
  // A row whose date has passed answers "This link has expired" to whoever clicks it —
  // Edit(만료일) or a Send is what reopens it.
  function renderExpiry(cell) {
    if (!cell.value) return <Muted>—</Muted>;
    if (new Date(cell.value) > new Date()) {
      return <span style={{ fontSize: 12 }}>{cell.text}</span>;
    }
    // A revoked row says who closed it and when: the CRA needs to know their own click
    // took, and a colleague needs to know this was somebody's decision, not the calendar.
    const { revokedAt, revokedByName } = cell.data;
    const revokedTitle = revokedAt
      ? `${fmtDate(new Date(revokedAt))}${revokedByName ? ` ${revokedByName}` : ""}님이 이 링크를 ` +
        "회수했습니다. 발송된 메일은 그대로 남아 있으나 링크는 열리지 않습니다. " +
        "Edit에서 만료일을 옮기거나 다시 발송하면 열립니다."
      : null;
    return (
      <ExpiredDate
        $revoked={!!revokedAt}
        title={revokedTitle
          || "이 사이트의 링크는 지금 열리지 않습니다. Edit에서 만료일을 옮기거나 다시 발송하면 열립니다."}
      >
        {cell.text}
      </ExpiredDate>
    );
  }

  function renderVersion(cell) {
    if (cell.value == null) return <Muted style={{ fontSize: 12 }}>미발송</Muted>;
    return <VerBadge title="이 사이트가 받은 버전입니다.">V{cell.value}</VerBadge>;
  }

  function renderStatus(cell) {
    return <StatusBadge $s={cell.value}>{STATUS_LABELS[cell.value] || cell.value}</StatusBadge>;
  }

  function renderRisk(cell) {
    if (!cell.value) return <Muted>—</Muted>;
    return <RiskBadge $r={cell.value}>{cell.value}</RiskBadge>;
  }

  function renderScore(cell) {
    if (cell.value == null) return <Muted>—</Muted>;
    return <span style={{ fontSize: 12 }}>{cell.value.toFixed(1)}</span>;
  }

  // "이 사이트에 자동 알림이 나가는가, 얼마나 더 나가는가." 판정은 reminderTemplates.js의
  // reminderState에 있고, API의 listDueReminders가 행을 고르는 규칙과 짝이다.
  function renderReminder(cell) {
    const s = reminderState(cell.data, reminderSettings);

    if (s.kind === "n/a") {
      return <Muted title="발송 전이거나 이미 제출한 사이트라 알림 대상이 아닙니다.">—</Muted>;
    }
    if (s.kind === "linkClosed") {
      return (
        <ReminderCell $off title="링크가 만료·회수되어 알림이 나가지 않습니다. 재발송하면 다시 대상이 됩니다.">
          링크 닫힘
        </ReminderCell>
      );
    }
    if (s.kind === "off") {
      return (
        <ReminderCell
          $off
          title={s.reason === "site"
            ? "이 사이트만 자동 알림을 껐습니다. Edit에서 다시 켤 수 있습니다."
            : "이 스터디의 자동 알림이 꺼져 있습니다. ⏰ 알림 Email Template에서 켤 수 있습니다."}
        >
          {s.reason === "site" ? "끔 (사이트)" : "끔"}
        </ReminderCell>
      );
    }
    if (s.kind === "done") {
      return (
        <ReminderCell $done title={`최대 ${s.max}회를 모두 보내 자동 알림이 끝났습니다. 더 보내려면 알림 버튼으로 직접 발송하세요.`}>
          완료 {s.sentCount}/{s.max}
        </ReminderCell>
      );
    }
    return (
      <ReminderCell
        $custom={s.custom}
        title={s.custom
          ? `이 사이트만 ${s.days}일 주기입니다. (스터디 기본값 ${reminderSettings?.intervalDays}일)`
          : `스터디 기본 주기 ${s.days}일마다, 최대 ${s.max}회까지 자동 발송됩니다.`}
      >
        {s.days}일마다 · {s.sentCount}/{s.max}
      </ReminderCell>
    );
  }

  // Once the site has submitted there is no route back to InProgress, so a resend would
  // only mint a new password for a link that opens the read-only submitted screen. Past
  // that point the mail log is the only action left. The API rejects the send too.
  function renderActions(cell) {
    const inv = cell.data;
    const canSend  = SENDABLE_STATUS.includes(inv.status);
    const canEdit  = EDITABLE_STATUS.includes(inv.status);
    const isResend = inv.status !== "Pending";
    const expired  = isLinkExpired(inv);
    // Nothing to close on a link that is already shut, so the button goes rather than
    // greying out — the Expires column already says why it is gone. mailBlocked covers
    // the frozen study: a closed-out study takes no writes at all (studyFrozenGate).
    //
    // Deliberately NOT `!expired`: isLinkExpired counts a missing expiry as closed, because
    // for a *send* that is the case worth renewing. The middleware disagrees — siteToken.js
    // only compares a date it actually has, so a row with no expiry opens forever. That row
    // is the one most worth revoking, and keying off `expired` would be the one place the
    // button hides. This asks the question the middleware asks, and matches the API's guard.
    const linkOpen  = !inv.siteTokenExpiry || new Date(inv.siteTokenExpiry) > new Date();
    const canRevoke = REVOCABLE_STATUS.includes(inv.status) && linkOpen && !mailBlocked;
    // 재촉은 이미 나간 링크가 아직 열려 있을 때만 성립한다. 여기서는 `linkOpen`이 아니라
    // `!expired`를 쓴다 — 회수와 달리, 만료일이 없는 행에 재촉을 보내는 것은 사이트에
    // 영영 열려 있는 링크를 다시 안내하는 일이라 재발송으로 정리하는 편이 맞다.
    // API(sendReminder)도 만료일이 없으면 거부한다.
    const canRemind = REMINDER_ELIGIBLE_STATUS.includes(inv.status) && !expired && !mailBlocked;
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        {canEdit && (
          <EditBtn
            onClick={() => setEditInv(inv)}
            title={inv.status === "Pending"
              ? "등록한 사이트 정보를 수정합니다."
              : "담당자 정보와 만료일을 수정합니다. Site 정보는 발송된 메일과 맞추기 위해 잠깁니다."}
          >
            Edit
          </EditBtn>
        )}
        {canSend && (
          <SendBtn
            disabled={mailBlocked}
            $expired={expired && !mailBlocked}
            onClick={() => openCompose(inv)}
            title={mailBlocked
              ? "Study가 Approved(Active) 상태가 되면 발송할 수 있습니다."
              : expired
                ? "이 사이트의 링크는 만료되어 지금 열리지 않습니다. 재발송하면 만료일이 " +
                  "갱신되고 새 비밀번호가 발급됩니다. 발송 없이 기간만 늘리려면 Edit을 쓰세요."
                : isResend
                  ? "메일을 다시 작성해 재발송합니다. 새 비밀번호가 발급됩니다."
                  : "메일 내용을 확인·수정한 뒤 발송합니다."}
          >
            {expired ? "Resend (만료)" : isResend ? "Resend" : "Send"}
          </SendBtn>
        )}
        {canRemind && (
          <RemindBtn
            onClick={() => setRemindInv(inv)}
            title="답변을 재촉하는 알림 메일을 지금 보냅니다. 비밀번호는 그대로 유지되며, 작성 중인 답변도 보존됩니다."
          >
            알림
          </RemindBtn>
        )}
        {canRevoke && (
          <RevokeBtn
            onClick={() => setRevokeInv(inv)}
            title="잘못 보낸 초대의 링크를 지금 닫습니다. 발송된 메일 자체는 회수되지 않으며, Edit에서 만료일을 옮기거나 재발송하면 다시 열립니다."
          >
            회수
          </RevokeBtn>
        )}
        <MailBtn
          $solo={!canSend && !canRevoke}
          $sent={inv.emailSentCount > 0}
          onClick={() => inv.emailSentCount > 0 && setEmailLogInv(inv)}
          title={inv.emailSentCount > 0 ? `${inv.emailSentCount}회 발송됨` : "발송 이력 없음"}
        >
          ✉ {inv.emailSentCount > 0 ? inv.emailSentCount : 0}
        </MailBtn>
      </div>
    );
  }

  const noDataText = canWork
    ? mailBlocked
      ? 'No sites added yet. Click "+ Add Site" to line up a site. (메일 발송은 Study가 Approved 상태가 된 뒤에 가능합니다.)'
      : 'No sites added yet. Click "+ Add Site" to invite a site.'
    : "No sites added yet.";

  return (
    <>
      <DataGrid
        dataSource={invitations}
        keyExpr="id"
        showBorders
        hoverStateEnabled
        onRowClick={handleRowClick}
        loadPanel={{ enabled: loading }}
        noDataText={noDataText}
        style={{ fontSize: 13 }}
      >
        <Toolbar>
          <Item location="before"
            render={() => <SectionTitle>Site Invitations ({invitations.length})</SectionTitle>}
          />
          {canWork && (
            <Item location="after">
              <Button
                text="✉ Email Template"
                stylingMode="outlined"
                onClick={() => setShowTemplate(true)}
                hint="이 스터디의 초대 메일 템플릿(한국어 / English)을 편집합니다."
              />
            </Item>
          )}
          {canWork && (
            <Item location="after">
              <Button
                text="🔒 개인정보 동의문"
                stylingMode="outlined"
                onClick={() => setShowConsent(true)}
                hint="Site가 설문을 열기 전에 동의해야 하는 개인정보 수집·이용 동의문(한국어 / English)을 편집합니다."
              />
            </Item>
          )}
          {canWork && (
            <Item location="after">
              <Button
                text="⏰ 알림 Email Template"
                stylingMode="outlined"
                onClick={() => setShowReminder(true)}
                hint="답변이 없는 사이트에 며칠마다 나가는 알림 메일의 문구(한국어 / English)와 발송 주기를 설정합니다."
              />
            </Item>
          )}
          {canWork && (
            <Item location="after">
              <Button
                text="+ Add Site"
                type="default"
                stylingMode="contained"
                onClick={() => setShowAdd(true)}
                hint={mailBlocked
                  ? "Site를 미리 등록해 둘 수 있습니다. 초대 메일은 Study가 Approved(Active) 상태가 된 뒤에 발송됩니다."
                  : undefined}
              />
            </Item>
          )}
        </Toolbar>

        {/* dataField 없는 언바운드 컬럼이라 정렬하려면 calculateSortValue가 필요하다. */}
        <Column name="site" caption="Site" minWidth={140}
          calculateCellValue={siteLabel}
          allowSorting={true}
          calculateSortValue={siteLabel}
          cellRender={(cell) => cell.value
            ? <span style={{ fontWeight: 500 }}>{cell.value}</span>
            : <Muted>—</Muted>} />
        <Column dataField="piName"   caption="PI"       width={110} />
        <Column dataField="piEmail"  caption="PI Email" minWidth={160} />
        <Column dataField="status"   caption="Status"   width={110} cellRender={renderStatus}
          calculateSortValue={(row) => STATUS_ORDER[row.status] ?? -1} />
        <Column dataField="sentVersionNo" caption="Version" width={90} cellRender={renderVersion} />
        <Column dataField="sentAt"          caption="Sent"      width={110}
          dataType="date" format="yyyy-MM-dd" cellRender={renderDate} />
        <Column dataField="submittedAt"     caption="Submitted" width={110}
          dataType="date" format="yyyy-MM-dd" cellRender={renderDate} />
        <Column dataField="siteTokenExpiry" caption="Expires"   width={125}
          dataType="date" format="yyyy-MM-dd" cellRender={renderExpiry} />
        {craEnabled && (
          <Column dataField="riskLevel" caption="Risk" width={90} cellRender={renderRisk}
            calculateSortValue={(row) => RISK_ORDER[row.riskLevel] ?? -1} />
        )}
        {craEnabled && (
          <Column dataField="scoreFinal" caption="Score" width={80} dataType="number"
            cellRender={renderScore} />
        )}
        {/* 자동 알림 상태. canWork 안에 두는 이유는 이 칼럼이 답하는 질문("내가 켠 알림이
            이 사이트에 나가고 있나")이 초대를 운영하는 사람의 것이기 때문이다. */}
        {canWork && (
          <Column caption="알림" width={112} allowSorting={false}
            cellRender={renderReminder} />
        )}
        {canWork && (
          <Column caption="Actions" width={355} allowSorting={false}
            cellRender={renderActions} />
        )}
      </DataGrid>

      {showAdd && (
        <AddSiteModal
          onSubmit={handleAdd}
          onClose={() => setShowAdd(false)}
        />
      )}

      {editInv && (
        <AddSiteModal
          invitation={editInv}
          onSubmit={handleEdit}
          onClose={() => setEditInv(null)}
        />
      )}

      {showTemplate && (
        <EmailTemplateModal
          sfqId={sfqId}
          onClose={() => setShowTemplate(false)}
        />
      )}

      {showConsent && (
        <ConsentTemplateModal
          sfqId={sfqId}
          onClose={() => setShowConsent(false)}
        />
      )}

      {/* onSaved: 알림 칼럼이 방금 바꾼 주기를 곧바로 반영하도록 — 저장한 값을 그대로
          받아 두면 목록을 다시 불러오지 않아도 된다. */}
      {showReminder && (
        <ReminderTemplateModal
          sfqId={sfqId}
          onSaved={setReminderSettings}
          onClose={() => setShowReminder(false)}
        />
      )}

      {composeInv && (
        <SendInvitationModal
          sfqId={sfqId}
          inv={composeInv}
          publicUrl={buildLink(composeInv)}
          fromName={myName}
          isResend={composeInv.status !== "Pending"}
          onSend={handleSend}
          onClose={() => setComposeInv(null)}
        />
      )}

      {remindInv && (
        <SendReminderModal
          sfqId={sfqId}
          inv={remindInv}
          publicUrl={buildLink(remindInv)}
          fromName={myName}
          onSend={handleSendReminder}
          onClose={() => setRemindInv(null)}
        />
      )}

      {revokeInv && (
        <RevokeLinkModal
          inv={revokeInv}
          onConfirm={handleRevoke}
          onClose={() => setRevokeInv(null)}
        />
      )}

      {emailLogInv && (
        <EmailLogModal
          sfqId={sfqId}
          invId={emailLogInv.id}
          siteName={emailLogInv.siteName || emailLogInv.siteCode}
          onClose={() => setEmailLogInv(null)}
        />
      )}
    </>
  );
}
